import axios from './axios.interceptors';
import {
  AircraftDefinitionRequest,
  AircraftDefinitionResponse,
  SeatMapResponse,
  AircraftStatus,
  PagedAircraftsList,
  AircraftStatusUpdateResponse,
} from 'app/types/service-types/AircraftTypes';

interface GetAllAircraftsParams {
  status?: AircraftStatus;
  page?: number;
  size?: number;
  sort?: string;
}

class AircraftService {

  public getAll = async (params?: GetAllAircraftsParams | AircraftStatus): Promise<PagedAircraftsList | AircraftDefinitionResponse[]> => {
    try {
      let url = '/api/aircrafts';
      const queryParams: string[] = [];
      
      // Handle legacy calls where status is passed directly as string
      if (typeof params === 'string') {
        queryParams.push(`status=${params}`);
        queryParams.push(`size=9999`); // Large size for non-paginated calls
      } else if (params) {
        if (params.status) queryParams.push(`status=${params.status}`);
        if (params.page !== undefined) queryParams.push(`page=${params.page}`);
        if (params.size !== undefined) queryParams.push(`size=${params.size}`);
        if (params.sort) queryParams.push(`sort=${params.sort}`);
      }
      
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }
      
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public getById = async (id: string): Promise<AircraftDefinitionResponse> => {
    try {
      const response = await axios.get(`/api/aircrafts/${id}`);
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public create = async (body: AircraftDefinitionRequest): Promise<any> => {
    try {
      const response = await axios.post('/api/aircrafts', body);
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public update = async (id: string, body: AircraftDefinitionRequest): Promise<any> => {
    try {
      const response = await axios.put(`/api/aircrafts/${id}`, body);
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public regenerateSeats = async (id: string, body: AircraftDefinitionRequest): Promise<any> => {
    try {
      const response = await axios.put(`/api/aircrafts/${id}/regenerate-seats`, body);
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public assignSeatClass = async (
    aircraftId: string,
    seatClassId: string,
    fromRow: number,
    toRow: number
  ): Promise<any> => {
    try {
      const response = await axios.post(
        `/api/aircrafts/${aircraftId}/assign-seat-class?seatClassId=${seatClassId}&fromRow=${fromRow}&toRow=${toRow}`
      );
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public updateStatus = async (id: string, status: AircraftStatus): Promise<AircraftStatusUpdateResponse> => {
    try {
      const response = await axios.patch(`/api/aircrafts/${id}/status?status=${status}`);
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public getSeatMap = async (flightId: string): Promise<SeatMapResponse> => {
    try {
      const response = await axios.get(`/api/flights/${flightId}/seat-map`);
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
}

export default new AircraftService();
